import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { motion } from "framer-motion";

const initial = { artist_name: "", email: "", track_link: "", genre: "Industrial Techno", request_type: "production", message: "" };
const genres = ["Industrial Techno", "Acid Techno", "Hypnotic Techno", "Hard Groove", "Melodic Techno", "Ambient / Electronica", "Other"];

const inputCls = "w-full bg-[#0c0c12] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#00f0ff] transition-colors";

export const ContactForm = ({ api }) => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${api}/contact`, form);
      toast.success("Transmission received. The label will get back to you.");
      setForm(initial);
    } catch {
      toast.error("Transmission failed. Check your email and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 border-t border-white/10 overflow-hidden">
      <div className="absolute bottom-0 right-0 w-[460px] h-[460px] bg-[#00ff66]/5 blur-[140px] rounded-full pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#ff0055] uppercase mb-3">// For Artists</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase text-white">
            Looking for Production<br />or <span className="text-[#00f0ff] neon-text-cyan">Visibility?</span>
          </h2>
          <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-md">
            Send us your demo or tell us what you need. Whether it's a full production deal or placement across the major music platforms, R-AN reviews every submission.
          </p>
          <div className="mt-8 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 space-y-2">
            <p><span className="text-[#00ff66]">▸</span> Demos reviewed within 7 days</p>
            <p><span className="text-[#00ff66]">▸</span> Streaming links preferred (SoundCloud / Drive)</p>
            <p><span className="text-[#00ff66]">▸</span> Unreleased material only</p>
          </div>
        </div>

        <motion.form
          data-testid="artist-demo-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 bg-[#0c0c12]/80 border border-white/10 p-6 sm:p-10 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Artist / Alias *</label>
              <input data-testid="demo-input-artist-name" required value={form.artist_name} onChange={set("artist_name")} placeholder="Your artist name" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Email *</label>
              <input data-testid="demo-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Demo / Track Link</label>
            <input data-testid="demo-input-track-link" value={form.track_link} onChange={set("track_link")} placeholder="SoundCloud, Dropbox or Drive link" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Primary Genre</label>
              <select data-testid="demo-select-genre" value={form.genre} onChange={set("genre")} className={inputCls}>
                {genres.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">I'm Looking For</label>
              <select data-testid="demo-select-request-type" value={form.request_type} onChange={set("request_type")} className={inputCls}>
                <option value="production">A production deal</option>
                <option value="visibility">Visibility on music platforms</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Message *</label>
            <textarea data-testid="demo-input-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your sound and what you're after..." className={inputCls} />
          </div>
          <button
            data-testid="demo-submit-button"
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-3 px-7 py-4 bg-[#ff0055] text-white font-mono-tech text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#00f0ff] hover:text-black transition-colors disabled:opacity-50"
          >
            <Send size={14} /> {sending ? "Transmitting..." : "Send Transmission"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
